import { useState, useEffect } from "react";
import {
  PlugsConnected, CheckCircle, Circle, ArrowClockwise, Lightning, Gear, Flask,
  BookOpen, ArrowSquareOut, Sparkle, Image as ImageIcon, VideoCamera, TextT, Microphone, Package,
  Gauge, Timer, CurrencyDollar, ListNumbers, Path, Strategy, MagicWand, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { EmptyState } from "@/promotions/components/EmptyState";
import { CardsSkeleton } from "@/promotions/components/Skeleton";
import { useMarketplace, marketplaceApi, imageEditorApi } from "../useCreative";

// SPRINT-105.5 — AI Provider Routing Intelligence.
const POLICY_LABEL = {
  balanced: "Balanced", best_quality: "Best Quality", lowest_cost: "Lowest Cost",
  fastest: "Fastest", lowest_latency: "Lowest Latency", highest_priority: "Highest Priority",
  fallback_only: "Fallback Only", manual: "Manual",
};
const POLICY_HINT = {
  balanced: "Weighs priority, cost, latency and reliability together.",
  best_quality: "Favours the most reliable, production-grade providers.",
  lowest_cost: "Routes to the cheapest eligible provider first.",
  fastest: "Optimises for the lowest recent average latency.",
  lowest_latency: "Optimises for the lowest recent average latency.",
  highest_priority: "Follows the configured provider priority order.",
  fallback_only: "Preserves resolver order — no scoring re-ranking.",
  manual: "Operator picks the provider explicitly per request.",
};
const STATUS_TONE = {
  online: "bg-success/15 text-success", offline: "bg-destructive/15 text-destructive",
  disabled: "bg-muted text-muted-foreground", unavailable: "bg-amber-500/15 text-amber-600",
};
const KIND_CAP = { creative_image: "image", creative_video: "video", creative_text: "text", creative_voice: "voice", creative_audio: "audio" };

function fmtMs(ms) { return ms == null ? "—" : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`; }
function fmtCost(c) { return c == null ? "—" : `$${Number(c).toFixed(4)}`; }
function fmtTime(t) { if (!t) return "Never"; try { return new Date(t).toLocaleString(); } catch { return t; } }

const KIND_ICON = {
  creative_image: ImageIcon, creative_video: VideoCamera, creative_text: TextT,
  creative_voice: Microphone, creative_audio: Microphone, creative_gateway: Lightning,
};
const KIND_LABEL = {
  creative_image: "Image", creative_video: "Video", creative_text: "Text",
  creative_voice: "Voice", creative_audio: "Audio", creative_gateway: "Gateway",
};
const FEATURE_ORDER = [
  "text", "image", "video", "audio", "speech", "vision", "prompt_enhancement", "editing",
  "inpainting", "outpainting", "upscaling", "background_removal", "structured_output",
  "streaming", "function_calling", "json_mode", "video_extension",
];
const FEATURE_LABEL = {
  text: "Text", image: "Image", video: "Video", audio: "Audio", speech: "Speech", vision: "Vision",
  prompt_enhancement: "Prompt Enhance", editing: "Editing", inpainting: "Inpainting",
  outpainting: "Outpainting", upscaling: "Upscaling", background_removal: "BG Removal",
  structured_output: "Structured", streaming: "Streaming", function_calling: "Functions",
  json_mode: "JSON Mode", video_extension: "Video Extend",
};

export default function Providers() {
  const { installed, catalog, policy, policies, loading, reload } = useMarketplace();
  const [manage, setManage] = useState(null);
  const [analytics, setAnalytics] = useState([]);

  const loadAnalytics = () => marketplaceApi.analytics().then((d) => setAnalytics(d.providers || [])).catch(() => setAnalytics([]));
  useEffect(() => { loadAnalytics(); }, [installed.length]);

  const online = installed.filter((p) => p.health?.healthy && p.settings?.enabled !== false).length;

  return (
    <div className="space-y-8" data-testid="ai-provider-marketplace">
      <PromoPageHeader
        overline="Creative Studio"
        title="AI Provider Marketplace"
        description="Manage every AI provider from one console — health, credentials, routing intelligence, analytics and capabilities. Provider-agnostic: new modules appear here automatically."
        actions={
          <button onClick={() => { reload(); loadAnalytics(); }} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid="marketplace-refresh">
            <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        }
      />

      {/* Operational summary */}
      {!loading && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="marketplace-summary">
          <Stat label="Installed" value={installed.length} Icon={Package} />
          <Stat label="Online" value={online} Icon={CheckCircle} tone="success" />
          <Stat label="Available" value={catalog.length} Icon={PlugsConnected} />
          <Stat label="Production" value={installed.filter((p) => p.settings?.mode === "production").length} Icon={Lightning} tone="brand" />
        </div>
      )}

      {/* Routing Intelligence console (SPRINT-105.5) */}
      {!loading && (
        <RoutingConsole policy={policy} policies={policies} installed={installed} onPolicyChanged={reload} />
      )}

      {/* Image Editor (SPRINT-PH-14): separate category — background removal/replacement */}
      <ImageEditorSettings />


      {/* Provider Analytics (SPRINT-107.0) */}
      {!loading && analytics.length > 0 && (
        <ProviderAnalytics rows={analytics} />
      )}

      {/* Installed */}
      <section className="space-y-4">
        <SectionTitle title="Installed Providers" hint="Registered in the backend — fully manageable" />
        {loading ? (
          <CardsSkeleton count={3} />
        ) : installed.length === 0 ? (
          <EmptyState Icon={PlugsConnected} title="No providers installed" description="Provider adapters self-register at startup." />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="installed-grid">
            {installed.map((p) => (
              <InstalledCard key={p.provider_id} p={p}
                open={manage === p.provider_id}
                onToggle={() => setManage(manage === p.provider_id ? null : p.provider_id)}
                onChanged={reload} />
            ))}
          </div>
        )}
      </section>

      {/* Catalog */}
      {!loading && catalog.length > 0 && (
        <section className="space-y-4">
          <SectionTitle title="Provider Catalog" hint="Available to install — plugs into the same architecture" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">
            {catalog.map((c) => <CatalogCard key={c.id} c={c} />)}
          </div>
        </section>
      )}
    </div>
  );
}

// ---- Image Editor settings (SPRINT-PH-14): pick the org Default Image Editor ----
function ImageEditorSettings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState("");
  const load = () => imageEditorApi.list().then(setData).catch(() => setData({ editors: [], default_image_editor: null }));
  useEffect(() => { load(); }, []);

  const choose = async (pid) => {
    if (!data) return;
    if (pid === data.default_image_editor) { toast.info(`${(data.editors.find((e) => e.provider_id === pid) || {}).name || pid} is already the default`); return; }
    setSaving(pid);
    try {
      await imageEditorApi.setDefault(pid);
      toast.success("Default Image Editor updated");
      await load();
    } catch { toast.error("Failed to set default image editor"); }
    setSaving("");
  };

  if (!data) return null;
  return (
    <section className="rounded-lg border border-border bg-card p-6" data-testid="image-editor-settings">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand/15 text-brand">
          <MagicWand size={18} weight="fill" />
        </div>
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Image Editor</h2>
          <p className="text-sm text-muted-foreground">
            A separate, local category for editing product images (background removal/replacement).
            It never competes with image/video generation providers — no API key, no cost.
          </p>
        </div>
      </div>

      <div className="mt-5">
        <div className="promo-overline mb-2">Default Image Editor</div>
        {data.editors.length === 0 ? (
          <p className="text-sm text-muted-foreground">No image editors installed.</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="image-editor-list">
            {data.editors.map((e) => {
              const active = e.provider_id === data.default_image_editor;
              return (
                <button key={e.provider_id} onClick={() => choose(e.provider_id)} disabled={!!saving}
                  className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-colors disabled:opacity-60 ${active ? "border-brand bg-brand/5" : "border-border hover:bg-accent"}`}
                  data-testid={`image-editor-option-${e.provider_id}`}>
                  <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${active ? "border-brand bg-brand" : "border-muted-foreground"}`}>
                    {active && <CheckCircle size={12} weight="fill" className="text-white" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="font-medium">{e.name}</span>
                      {active && <span className="rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-medium text-brand" data-testid={`image-editor-default-badge-${e.provider_id}`}>Default</span>}
                    </span>
                    <span className="mt-0.5 block font-mono text-[11px] text-muted-foreground">{e.provider_id}</span>
                    <span className="mt-1 flex items-center gap-1.5 text-[11px]">
                      <span className={`h-1.5 w-1.5 rounded-full ${e.health?.healthy ? "bg-success" : "bg-destructive"}`} />
                      <span className="text-muted-foreground">{e.health?.detail || (e.health?.healthy ? "Online" : "Offline")}</span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}


function ProviderAnalytics({ rows }) {
  const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);
  const ms = (v) => (v == null ? "—" : `${Math.round(v)}ms`);
  const hsTone = (s) => (s >= 80 ? "text-success" : s >= 50 ? "text-warning" : "text-destructive");
  return (
    <section className="space-y-4" data-testid="provider-analytics">
      <SectionTitle title="Provider Analytics" hint="Live operational metrics from the Routing Engine — enterprise at a glance" />
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2.5 font-medium">Provider</th>
              <th className="px-3 py-2.5 font-medium">Health</th>
              <th className="px-3 py-2.5 font-medium">Requests</th>
              <th className="px-3 py-2.5 font-medium">Success</th>
              <th className="px-3 py-2.5 font-medium">Failure</th>
              <th className="px-3 py-2.5 font-medium">Avg latency</th>
              <th className="px-3 py-2.5 font-medium">Avg gen time</th>
              <th className="px-3 py-2.5 font-medium">Avg cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.provider_id} className="border-b border-border/50 last:border-0" data-testid={`analytics-row-${r.provider_id}`}>
                <td className="px-4 py-2.5">
                  <div className="font-medium">{r.name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{r.provider_id}</div>
                </td>
                <td className="px-3 py-2.5"><span className={`font-semibold ${hsTone(r.health_score)}`}>{r.health_score}</span></td>
                <td className="px-3 py-2.5">{r.request_count}</td>
                <td className="px-3 py-2.5 text-success">{pct(r.success_rate)}</td>
                <td className="px-3 py-2.5 text-destructive">{pct(r.failure_rate)}</td>
                <td className="px-3 py-2.5">{ms(r.avg_latency_ms)}</td>
                <td className="px-3 py-2.5">{ms(r.avg_generation_time_ms)}</td>
                <td className="px-3 py-2.5">{r.avg_cost == null ? "—" : `$${Number(r.avg_cost).toFixed(4)}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---- Routing Intelligence console: org policy + dry-run preview -------------
function RoutingConsole({ policy, policies, installed, onPolicyChanged }) {
  const opts = policies && policies.length ? policies : Object.keys(POLICY_LABEL);
  const [saving, setSaving] = useState(false);
  const kinds = Array.from(new Set(installed.map((p) => KIND_CAP[p.kind]).filter(Boolean)));

  const change = async (next) => {
    if (next === policy) return;
    setSaving(true);
    try {
      await marketplaceApi.setPolicy(next);
      toast.success(`Routing policy set to ${POLICY_LABEL[next] || next}`);
      onPolicyChanged?.();
    } catch { toast.error("Failed to update routing policy"); }
    setSaving(false);
  };

  return (
    <section className="rounded-lg border border-border bg-card p-6" data-testid="routing-console">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-ai/15 text-ai">
            <Strategy size={18} weight="fill" />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Routing Intelligence</h2>
            <p className="text-sm text-muted-foreground">Automatic provider selection with policy scoring, quota gating and a fallback chain.</p>
          </div>
        </div>
        <span className="hidden items-center gap-1 rounded-full bg-ai/10 px-2.5 py-1 text-[11px] font-medium text-ai sm:flex" data-testid="routing-active-policy">
          <Path size={12} /> {POLICY_LABEL[policy] || policy}
        </span>
      </div>

      {/* Policy selector */}
      <div className="mt-5">
        <div className="promo-overline mb-2">Routing Policy</div>
        <div className="flex flex-wrap gap-2" data-testid="routing-policy-picker">
          {opts.map((pol) => {
            const active = pol === policy;
            return (
              <button key={pol} onClick={() => change(pol)} disabled={saving}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors disabled:opacity-50 ${active ? "border-ai bg-ai/10 text-ai font-medium" : "border-border text-muted-foreground hover:bg-accent hover:text-foreground"}`}
                data-testid={`routing-policy-${pol}`}>
                {POLICY_LABEL[pol] || pol}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-muted-foreground" data-testid="routing-policy-hint">{POLICY_HINT[policy] || ""}</p>
      </div>

      <RoutingPreview kinds={kinds} policy={policy} />
    </section>
  );
}

// ---- Routing Preview: dry-run decision (no generation, no provider call) ----
function RoutingPreview({ kinds, policy }) {
  const [kind, setKind] = useState(kinds[0] || "image");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await marketplaceApi.routingPreview({ kind, operation: "generate" });
      setResult(r);
    } catch { toast.error("Routing preview failed"); }
    setBusy(false);
  };

  return (
    <div className="mt-5 border-t border-border/60 pt-5" data-testid="routing-preview">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="promo-overline mb-1">Simulate a request (dry-run)</div>
          <select value={kind} onChange={(e) => setKind(e.target.value)}
            className="rounded-md border border-border bg-card px-3 py-1.5 text-sm capitalize" data-testid="routing-preview-kind">
            {(kinds.length ? kinds : ["image", "video", "text"]).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <button onClick={run} disabled={busy}
          className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          data-testid="routing-preview-run">
          <MagicWand size={14} className={busy ? "animate-pulse" : ""} /> Preview Route
        </button>
        <span className="text-xs text-muted-foreground">No generation runs — this only evaluates routing.</span>
      </div>

      {result && (
        <div className="mt-4 space-y-4 rounded-md border border-border bg-muted/20 p-4" data-testid="routing-preview-result">
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <Meta label="Selected Provider" value={result.selected_provider || "None eligible"} />
            <Meta label="Policy" value={POLICY_LABEL[result.policy] || result.policy} />
            <Meta label="Capability" value={result.capability} />
            <Meta label="Capability Match" value={result.capability_match ? "Yes" : "No"} />
            <Meta label="Health" value={result.health || "—"} />
            <Meta label="Est. Cost" value={fmtCost(result.est_cost)} />
            <Meta label="Est. Latency" value={fmtMs(result.avg_latency_ms)} />
            <Meta label="Manual Override" value={result.manual_override ? "Yes" : "No"} />
          </div>
          <div>
            <div className="promo-overline mb-1">Reason</div>
            <p className="text-sm" data-testid="routing-preview-reason">{result.reason}</p>
          </div>
          <div>
            <div className="promo-overline mb-2">Fallback Chain</div>
            {result.fallback_chain?.length ? (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="routing-preview-chain">
                {result.fallback_chain.map((pid, i) => (
                  <span key={pid} className="flex items-center gap-1.5">
                    {i > 0 && <Path size={12} className="text-muted-foreground" />}
                    <span className={`rounded-full px-2.5 py-1 text-xs font-mono ${i === 0 ? "bg-ai/15 text-ai font-medium" : "border border-border bg-card text-muted-foreground"}`}>
                      {i === 0 ? "① " : `${i + 1}. `}{pid}
                    </span>
                  </span>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">No eligible providers in the chain.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function InstalledCard({ p, open, onToggle, onChanged }) {
  const Icon = KIND_ICON[p.kind] || Lightning;
  const s = p.settings || {};
  const healthy = p.health?.healthy;
  return (
    <div className="rounded-lg border border-border bg-card p-6" data-testid={`provider-${p.provider_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-ai/15 text-ai">
            <Icon size={18} weight="fill" />
          </div>
          <div>
            <h3 className="font-medium tracking-tight">{p.name}</h3>
            <p className="font-mono text-xs text-muted-foreground">{p.provider_id}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${healthy ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"}`} data-testid={`provider-health-${p.provider_id}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${healthy ? "bg-success" : "bg-destructive"}`} /> {healthy ? "Online" : "Offline"}
          </span>
          <ModeBadge mode={s.mode} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <Meta label="Kind" value={KIND_LABEL[p.kind] || p.kind} />
        <Meta label="Version" value={p.version} />
        <Meta label="Priority" value={s.priority} />
        <Meta label="Model" value={s.default_model || "default"} />
      </div>

      <div className="mt-4">
        <div className="promo-overline mb-2">Capability Matrix</div>
        <div className="flex flex-wrap gap-1.5" data-testid={`provider-caps-${p.provider_id}`}>
          {FEATURE_ORDER.filter((f) => (p.features || {})[f] !== undefined).map((f) => {
            const on = (p.features || {})[f];
            return (
              <span key={f} className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${on ? "border border-brand/20 bg-brand/10 text-brand" : "border border-border bg-muted/40 text-muted-foreground/60"}`}>
                {on ? <CheckCircle size={11} weight="fill" /> : <Circle size={11} />} {FEATURE_LABEL[f] || f}
              </span>
            );
          })}
        </div>
      </div>

      <RoutingBlock p={p} />

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border/60 pt-4">
        <button onClick={onToggle} className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground transition-opacity hover:opacity-90" data-testid={`provider-manage-${p.provider_id}`}>
          <Gear size={14} /> {open ? "Close" : "Manage"}
        </button>
        <TestButton pid={p.provider_id} />
        <LinkButton href={p.docs} Icon={BookOpen} label="Docs" testid={`provider-docs-${p.provider_id}`} />
        <LinkButton href={p.portal} Icon={ArrowSquareOut} label="Developer Portal" testid={`provider-portal-${p.provider_id}`} />
      </div>

      {open && <ManagePanel p={p} onChanged={onChanged} />}
    </div>
  );
}

function RoutingBlock({ p }) {
  const r = p.routing || {};
  const status = r.status || (p.health?.healthy ? "online" : "offline");
  const tone = STATUS_TONE[status] || STATUS_TONE.unavailable;
  const rank = r.fallback_rank;
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const runValidate = async () => {
    setValidating(true);
    try {
      const v = await marketplaceApi.validate(p.provider_id);
      setValidation(v);
      v.validated ? toast.success(`${p.name}: production key validated`)
        : v.has_credentials ? toast.error(`${p.name}: key present but health check failed`)
          : toast.info(`${p.name}: demo mode — no API key configured`);
    } catch { toast.error("Validation failed"); }
    setValidating(false);
  };
  const mode = r.mode || p.settings?.mode || "demo";
  return (
    <div className="mt-4 rounded-md border border-border bg-muted/20 p-4" data-testid={`provider-routing-${p.provider_id}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="promo-overline flex items-center gap-1.5"><Path size={12} /> Routing & Operations</div>
        <div className="flex items-center gap-1.5">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${mode === "production" ? "bg-brand/15 text-brand" : "bg-muted text-muted-foreground"}`} data-testid={`provider-mode-badge-${p.provider_id}`}>{mode}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${tone}`} data-testid={`provider-routing-status-${p.provider_id}`}>{status}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <RMeta Icon={Gauge} label="Routing Score" value={r.score != null ? `${r.score}/100` : "—"} testid={`provider-routing-score-${p.provider_id}`} />
        <RMeta Icon={ListNumbers} label="Priority" value={r.priority ?? p.settings?.priority ?? "—"} />
        <RMeta Icon={Path} label="Fallback Rank" value={rank != null ? `#${rank + 1}` : "—"} />
        <RMeta Icon={Timer} label="Avg Latency" value={fmtMs(r.avg_latency_ms)} />
        <RMeta Icon={CurrencyDollar} label="Est. Cost" value={fmtCost(r.est_cost)} />
        <RMeta Icon={Package} label="Quota" value={r.quota ?? "∞"} />
        <RMeta Icon={Lightning} label="Requests" value={r.requests_served ?? 0} />
        <RMeta Icon={CheckCircle} label="Last Success" value={fmtTime(r.last_success)} testid={`provider-last-success-${p.provider_id}`} />
      </div>
      {r.last_error && (
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid={`provider-last-error-${p.provider_id}`}>
          Last error: {r.last_error.message || String(r.last_error)}
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button onClick={runValidate} disabled={validating} className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50" data-testid={`provider-validate-${p.provider_id}`}>
          <Gauge size={12} className={validating ? "animate-pulse" : ""} /> Validate API
        </button>
        {validation && (
          <span className="text-xs text-muted-foreground" data-testid={`provider-validation-result-${p.provider_id}`}>
            {validation.validated ? "✓ Production validated" : validation.has_credentials ? "Key present · unhealthy" : `Demo${validation.credential_env ? ` · set ${validation.credential_env} to go live` : ""}`}
          </span>
        )}
      </div>
    </div>
  );
}

function RMeta({ Icon, label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="promo-overline flex items-center gap-1"><Icon size={11} /> {label}</div>
      <div className="mt-0.5 truncate">{value ?? "—"}</div>
    </div>
  );
}

// PixelBin's per-org "Cloud Name" (Creative Studio → Marketplace → PixelBin →
// Manage). Not a secret — persisted as a plain field on the same per-org
// provider settings document as enabled/priority/etc (PUT
// /marketplace/{provider_id}/settings), never the API-key Vault flow.
const PIXELBIN_PROVIDER_ID = "creative.image.pixelbin-watermark";

function ManagePanel({ p, onChanged }) {
  const s = p.settings || {};
  const [form, setForm] = useState({
    enabled: s.enabled !== false, priority: s.priority ?? 100,
    default_model: s.default_model || "", daily_quota: s.daily_quota || "",
    rate_limit_per_min: s.rate_limit_per_min || "", cost_per_unit: s.cost_per_unit || "",
    mode: s.mode || "demo",
  });
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  // Re-read on every (re)open of this panel — same pattern as `form` above —
  // so the value is reloaded correctly when Manage is closed and reopened.
  const [cloudName, setCloudName] = useState(s.cloud_name || "");
  const [savingCloudName, setSavingCloudName] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await marketplaceApi.saveSettings(p.provider_id, {
        enabled: form.enabled, priority: Number(form.priority) || 100,
        default_model: form.default_model || null,
        daily_quota: form.daily_quota ? Number(form.daily_quota) : null,
        rate_limit_per_min: form.rate_limit_per_min ? Number(form.rate_limit_per_min) : null,
        cost_per_unit: form.cost_per_unit ? Number(form.cost_per_unit) : null,
        mode: form.mode,
      });
      toast.success(`${p.name} settings saved`);
      onChanged?.();
    } catch { toast.error("Failed to save settings"); }
    setSaving(false);
  };
  const saveKey = async () => {
    try {
      await marketplaceApi.setApiKey(p.provider_id, apiKey);
      toast.success(apiKey ? "API key stored (encrypted)" : "API key cleared");
      setApiKey("");
      onChanged?.();
    } catch { toast.error("Failed to store API key"); }
  };
  const saveCloudName = async () => {
    setSavingCloudName(true);
    try {
      // Same settings endpoint/architecture as the "Save settings" button
      // above — just a separate save action for this one field, saved via
      // the same PUT /marketplace/{provider_id}/settings the other provider
      // settings already use.
      await marketplaceApi.saveSettings(p.provider_id, { cloud_name: cloudName || null });
      toast.success("PixelBin Cloud Name saved");
      onChanged?.();
    } catch { toast.error("Failed to save PixelBin Cloud Name"); }
    setSavingCloudName(false);
  };

  return (
    <div className="mt-4 space-y-4 rounded-md border border-border bg-muted/20 p-4" data-testid={`provider-settings-${p.provider_id}`}>
      <div className="promo-overline">Settings</div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Enabled">
          <select value={form.enabled ? "1" : "0"} onChange={(e) => setForm({ ...form, enabled: e.target.value === "1" })} className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm" data-testid={`provider-enabled-${p.provider_id}`}>
            <option value="1">Enabled</option><option value="0">Disabled</option>
          </select>
        </Field>
        <Field label="Mode">
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm" data-testid={`provider-mode-${p.provider_id}`}>
            <option value="demo">Demo</option><option value="production">Production</option>
          </select>
        </Field>
        <Field label="Priority"><Input value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} type="number" testid={`provider-priority-${p.provider_id}`} /></Field>
        <Field label="Default Model"><Input value={form.default_model} onChange={(v) => setForm({ ...form, default_model: v })} placeholder="default" /></Field>
        <Field label="Daily Quota"><Input value={form.daily_quota} onChange={(v) => setForm({ ...form, daily_quota: v })} type="number" placeholder="∞" /></Field>
        <Field label="Rate Limit /min"><Input value={form.rate_limit_per_min} onChange={(v) => setForm({ ...form, rate_limit_per_min: v })} type="number" placeholder="∞" /></Field>
        <Field label="Cost / unit ($)"><Input value={form.cost_per_unit} onChange={(v) => setForm({ ...form, cost_per_unit: v })} type="number" placeholder="0.00" /></Field>
      </div>
      <button onClick={save} disabled={saving} className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50" data-testid={`provider-save-${p.provider_id}`}>
        {saving ? "Saving…" : "Save settings"}
      </button>

      <div className="border-t border-border/60 pt-3">
        <div className="promo-overline mb-1">Credentials</div>
        <p className="mb-2 text-xs text-muted-foreground">
          {p.settings?.has_api_key ? "An encrypted API key is stored for this organization." : "No API key stored — running in demo mode."}
          {p.settings?.credential_env ? ` (env fallback: ${p.settings.credential_env})` : ""}
        </p>
        <div className="flex items-center gap-2">
          <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Paste API key to go live" className="flex-1 rounded-md border border-border bg-card px-2 py-1.5 font-mono text-xs" data-testid={`provider-apikey-${p.provider_id}`} />
          <button onClick={saveKey} className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent" data-testid={`provider-savekey-${p.provider_id}`}>
            {apiKey ? "Store key" : "Clear key"}
          </button>
        </div>

        {/* PixelBin-only: per-org Cloud Name — not a secret, so a plain text
            input (never type="password"), saved/reloaded independently of
            the API key above. */}
        {p.provider_id === PIXELBIN_PROVIDER_ID && (
          <div className="mt-3">
            <div className="promo-overline mb-1">Cloud Name PixelBin</div>
            <p className="mb-2 text-xs text-muted-foreground">
              {s.cloud_name ? "Configured for this organization." : "Not configured — PixelBin requests will fail until this organization sets its Cloud Name."}
            </p>
            <div className="flex items-center gap-2">
              <input type="text" value={cloudName} onChange={(e) => setCloudName(e.target.value)}
                placeholder="e.g. billowing-sound-16bd2d"
                className="flex-1 rounded-md border border-border bg-card px-2 py-1.5 font-mono text-xs"
                data-testid={`provider-pixelbin-cloudname-${p.provider_id}`} />
              <button onClick={saveCloudName} disabled={savingCloudName}
                className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
                data-testid={`provider-pixelbin-savecloudname-${p.provider_id}`}>
                {savingCloudName ? "Saving…" : "Save configuration"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TestButton({ pid }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await marketplaceApi.test(pid);
      r.ok ? toast.success(`Provider healthy — ${r.detail || "ok"}`) : toast.error(`Test failed — ${r.detail || "unhealthy"}`);
    } catch { toast.error("Test failed"); }
    setBusy(false);
  };
  return (
    <button onClick={run} disabled={busy} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50" data-testid={`provider-test-${pid}`}>
      <Flask size={14} className={busy ? "animate-pulse" : ""} /> Test
    </button>
  );
}

function CatalogCard({ c }) {
  const Icon = KIND_ICON[c.kind] || Sparkle;
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/20 p-5 opacity-90" data-testid={`catalog-${c.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-muted-foreground"><Icon size={16} /></div>
          <div>
            <h4 className="text-sm font-medium">{c.name}</h4>
            <p className="text-[11px] text-muted-foreground">{KIND_LABEL[c.kind] || c.kind}</p>
          </div>
        </div>
        <span className="rounded-full border border-border bg-card px-2 py-0.5 text-[10px] text-muted-foreground">Not Installed</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1">
        {(c.features || []).slice(0, 6).map((f) => (
          <span key={f} className="rounded border border-border bg-card px-1.5 py-0.5 text-[10px] text-muted-foreground">{FEATURE_LABEL[f] || f}</span>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button disabled className="cursor-not-allowed rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground/60" data-testid={`catalog-install-${c.id}`}>Install Module · Coming Soon</button>
        <LinkButton href={c.docs} Icon={BookOpen} label="Docs" testid={`catalog-docs-${c.id}`} small />
      </div>
    </div>
  );
}

// --- primitives ---
function SectionTitle({ title, hint }) {
  return (
    <div>
      <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
      <p className="text-sm text-muted-foreground">{hint}</p>
    </div>
  );
}
function Stat({ label, value, Icon, tone }) {
  const c = { success: "text-success", brand: "text-brand" }[tone] || "text-ai";
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="promo-overline">{label}</span><Icon size={16} className={c} />
      </div>
      <div className="mt-1 text-2xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}
function ModeBadge({ mode }) {
  const prod = mode === "production";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${prod ? "bg-brand/15 text-brand" : "bg-muted text-muted-foreground"}`}>
      {prod ? "Production" : "Demo"}
    </span>
  );
}
function Meta({ label, value }) {
  return (
    <div>
      <div className="promo-overline">{label}</div>
      <div className="mt-0.5 truncate capitalize">{value ?? "—"}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <div className="promo-overline mb-1">{label}</div>
      {children}
    </div>
  );
}
function Input({ value, onChange, type = "text", placeholder, testid }) {
  return (
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm" data-testid={testid} />
  );
}
function LinkButton({ href, Icon, label, testid, small }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
      className={`flex items-center gap-1.5 rounded-md border border-border text-muted-foreground hover:bg-accent hover:text-foreground ${small ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm"}`}>
      <Icon size={small ? 12 : 14} /> {label}
    </a>
  );
}
